/**
 * The header's page search (Ctrl+K): finding the pages of the menu by name.
 *
 * The entries are the sidebar's own - the caller builds them from `presentMenuItems` - so this only
 * decides which of them match what was typed, and in what order.
 */

/** One page the search can offer. */
export interface PageSearchEntry {
    /** The page key, e.g. `statistics` or `pred_casa`. */
    key: string;
    path: string;
    /** The label as the sidebar shows it, in the current language. */
    label: string;
    /** The entry's own English name, so the page is found in English whatever the language. */
    name: string;
}

/**
 * Lower case, without diacritics, single-spaced: "Încasări" and "incasari" compare equal, and so do
 * the Romanian comma (ș, ț) and cedilla (ş, ţ) forms and the Hungarian ő and ű.
 */
export function normalizeSearchText(text: string): string {
    return text
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

/** How well one entry matches: lower is better, `null` is no match. */
function matchRank(entry: PageSearchEntry, query: string, words: readonly string[]): number | null {
    const label = normalizeSearchText(entry.label);
    const haystack = `${label} ${normalizeSearchText(entry.name)}`;

    if (!words.every(word => haystack.includes(word))) {
        return null;
    }
    if (label.startsWith(query)) {
        return 0;
    }
    if (label.split(' ').some(labelWord => labelWord.startsWith(words[0]))) {
        return 1;
    }
    return 2;
}

/**
 * The entries matching `query`, best first; entries that match equally keep their menu order.
 *
 * Every word typed has to occur in the label or in the English name. A label that starts with what
 * was typed ranks first, then one with a word starting with it, then the rest. An empty query lists
 * every entry, in menu order.
 */
export function searchPages<T extends PageSearchEntry>(
    entries: readonly T[],
    query: string,
    limit = 10,
): T[] {
    const normalized = normalizeSearchText(query);

    if (normalized === '') {
        return entries.slice(0, limit);
    }

    const words = normalized.split(' ');

    return entries
        .map((entry, index) => ({ entry, index, rank: matchRank(entry, normalized, words) }))
        .filter((match): match is { entry: T; index: number; rank: number } => match.rank !== null)
        .sort((left, right) => left.rank - right.rank || left.index - right.index)
        .slice(0, limit)
        .map(match => match.entry);
}

/**
 * Whether a key press opens the page search: Ctrl+K, or Cmd+K on macOS, without Alt or Shift.
 *
 * `code` as well as `key`, because a layout that does not put a K on that key (Cyrillic, say)
 * still reports `KeyK` for it.
 *
 * `key` and `code` may be missing: Chrome fires a plain `keydown` `Event` with neither when it
 * autofills a field, and reading `key.toLowerCase()` off that threw on every autofill.
 */
export function isPageSearchShortcut(event: {
    key?: string;
    code?: string;
    altKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    shiftKey: boolean;
}): boolean {
    const isK = event.key?.toLowerCase() === 'k' || event.code === 'KeyK';
    return isK && (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey;
}
